<!-- VideoPlayerPage.vue -->
<template>
  <div class="page">
    <div
      class="video-container"
      ref="videoContainer"
      @mouseenter="isHovered = true; syncArchiveAudio()"
      @mouseleave="isHovered = false; syncArchiveAudio()"
    >
      <video ref="videoEl" class="video" playsinline></video>

      <!-- Loading spinner -->
      <div v-if="(isBuffering || autoPaused) && !hlsError" class="loading-spinner">
        <div class="spinner"></div>
      </div>

      <!-- Error display -->
      <div v-if="hlsError" class="error-display">
        <div class="error-icon">
          <span class="mdi mdi-alert-circle"></span>
        </div>
        <div class="error-title">Ошибка загрузки видео</div>
        <div class="error-message">{{ hlsError }}</div>
        <button class="error-retry" @click="retryLoad">
          <span class="mdi mdi-refresh"></span> Повторить
        </button>
      </div>

      <!-- Timestamp display at top-right -->
      <div class="timestamp-display">
        {{ formattedTimestamp }} 
      </div>

      <!-- Zoom level indicator -->
      <div v-if="videoZoomLevel > 1" class="zoom-indicator">
        {{ videoZoomLevel }}x
      </div>

      <!-- Debug Menu -->
      <div v-if="showDebugMenu" class="debug-menu">
        <div class="debug-header">
          <span>Debug Info</span>
          <button @click="showDebugMenu = false" class="debug-close">×</button>
        </div>
        <div class="debug-content">
          <div class="debug-section">
            <div class="debug-label">Playlist URL:</div>
            <div class="debug-value">{{ debugInfo.playlistUrl || 'N/A' }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Current Day:</div>
            <div class="debug-value">{{ debugInfo.currentDay || 'N/A' }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Current Segment:</div>
            <div class="debug-value">{{ debugInfo.currentSegment || 'N/A' }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Segment PDT:</div>
            <div class="debug-value">{{ debugInfo.segmentPDT || 'N/A' }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Video State:</div>
            <div class="debug-value">{{ debugInfo.videoState }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Buffering:</div>
            <div class="debug-value" :class="{ 'buffering-active': isBuffering }">
              {{ isBuffering ? 'YES' : 'NO' }}
            </div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Current Time:</div>
            <div class="debug-value">{{ debugInfo.currentTime }}s / {{ debugInfo.duration }}s</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Buffered Ranges:</div>
            <div class="debug-value">{{ debugInfo.buffered }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">HLS Level:</div>
            <div class="debug-value">{{ debugInfo.hlsLevel }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Last HLS Event:</div>
            <div class="debug-value">{{ debugInfo.lastHlsEvent || 'N/A' }}</div>
          </div>
          <div v-if="hlsError" class="debug-section">
            <div class="debug-label">HLS Error:</div>
            <div class="debug-value" style="color: #ff6b6b;">{{ hlsError }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Last HLS Error:</div>
            <div class="debug-value">{{ debugInfo.lastHlsError || 'N/A' }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Network State:</div>
            <div class="debug-value">{{ debugInfo.networkState }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Ready State:</div>
            <div class="debug-value">{{ debugInfo.readyState }}</div>
          </div>
          <div class="debug-section">
            <div class="debug-label">Timeline Position:</div>
            <div class="debug-value">{{ debugInfo.timelinePosition }}</div>
          </div>
        </div>
      </div>

      <!-- Camera navigation buttons -->
      <div class="camera-nav">
        <button class="camera-nav-btn camera-nav-prev" @click="onCamMove('left')" title="Previous Camera">
          <span class="mdi mdi-chevron-left"></span>
        </button>
        <button class="camera-nav-btn camera-nav-next" @click="onCamMove('right')" title="Next Camera">
          <span class="mdi mdi-chevron-right"></span>
        </button>
      </div>

      <!-- Controls overlay at bottom of video -->
      <div class="controls-overlay">
        <Controls :camera-id="cameraId" :server-tz="serverTz" v-model="centerEpochMs" :timeline-data="timelineData"
          :url="url" :width="timelineWidth" :video-element="videoEl" :container-element="videoContainer"
          :controls-ready="controlsReady"
          @seek="seekToEpoch" @video-zoom="handleVideoZoom" @playback-speed="handlePlaybackSpeed" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Hls from 'hls.js';
import Controls from './Controls.vue';
import {
  normalizeTimestampMs,
  getDayString,
  snapToNearestSegment,
  calculateVideoPosition,
} from './timeline-seek.js';
import { useDefaultStore } from '@/store';
import {
  tryPlayerAutoplay,
  detectVideoElementHasAudio,
  detectHlsHasAudio,
  applyPlayerAudio,
  playerMuted,
  playerVolume,
} from '@/services/player-audio';
const route = useRoute();
const router = useRouter();
const store = useDefaultStore();

// Make these reactive to route changes
const divisionUuid = computed(() => String(route.params.division || ''));
const cameraId = computed(() => String(route.params.cam || ''));

// Watch for camera changes and reset initialization
watch(() => route.params.cam, async (newCam, oldCam) => {
  if (newCam && newCam !== oldCam) {
    // Reset initialization flag when camera changes
    initialLoadComplete.value = false;
    videoReady.value = false;
    timelineDataLoaded.value = false;
    hasAudioTrack.value = false;

    // Reload timeline data for new camera
    try {
      await fetchServerTimezone();
    } catch (e) {
      console.error('Error reloading for new camera:', e);
    }
  }
});

const videoEl = ref(null);
const videoContainer = ref(null);
const hlsRef = ref(null);
const usingHls = ref(false);
const currentDay = ref('');
const centerEpochMs = ref(Date.now()); // Will be set to start of day after loading
const timelineWidth = ref(900);
const serverTz = ref('Europe/Moscow');
const timelineData = ref([]);
const initialLoadComplete = ref(false);
const isBuffering = ref(true);
const videoZoomLevel = ref(1); // Track current zoom level for indicator
const autoPaused = ref(false);
const timelineDataLoaded = ref(false);
const videoReady = ref(false);
const hasAudioTrack = ref(false);
const isHovered = ref(false);

let currentPlaylistUrl = '';

function syncArchiveAudio() {
  if (!videoEl.value) return;
  applyPlayerAudio(videoEl.value, hasAudioTrack.value, isHovered.value);
}

watch([playerMuted, playerVolume, isHovered], syncArchiveAudio);

watch(hasAudioTrack, (hasAudio) => {
  if (hasAudio) syncArchiveAudio();
});

// Debug menu state
const showDebugMenu = ref(false);
const debugInfo = ref({
  playlistUrl: '',
  currentDay: '',
  currentSegment: '',
  segmentPDT: '',
  videoState: 'idle',
  currentTime: 0,
  duration: 0,
  buffered: '',
  hlsLevel: 'N/A',
  networkState: 'N/A',
  readyState: 'N/A',
  timelinePosition: '',
  lastHlsEvent: '',
  lastHlsError: ''
});

const division = computed(() => store.allDivisions.find(el => el.guid === divisionUuid.value));
const url = computed(() => {
  if (window.location.host === '188.225.58.173:3552') {
    return division.value ? `//${division.value.address}` : ''
  }
  return division.value ? `//${division.value.url}` : ''
});

// Computed property to check if controls should be enabled
const controlsReady = computed(() => timelineDataLoaded.value);

// Format timestamp in DD.MM.YYYY, HH:MM:SS format
const formattedTimestamp = computed(() => {
  try {
    const date = new Date(centerEpochMs.value);
    const fmt = new Intl.DateTimeFormat(undefined, {
      timeZone: serverTz.value,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = fmt.formatToParts(date);
    const day = parts.find(p => p.type === 'day')?.value || '00';
    const month = parts.find(p => p.type === 'month')?.value || '00';
    const year = parts.find(p => p.type === 'year')?.value || '0000';
    const hour = parts.find(p => p.type === 'hour')?.value || '00';
    const minute = parts.find(p => p.type === 'minute')?.value || '00';
    const second = parts.find(p => p.type === 'second')?.value || '00';

    return `${day}.${month}.${year}, ${hour}:${minute}:${second}`;
  } catch {
    return '--.--.----, --:--:--';
  }
});

// Get start of day (00:00) in server timezone for a given date
function getStartOfDayInServerTz(dateMs, timezone) {
  try {
    const date = new Date(dateMs);

    // Get the date parts in the server timezone
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Format returns YYYY-MM-DD
    const dateStr = fmt.format(date);

    // Create a Date object at midnight in the server timezone
    // We'll use the local Date constructor but adjust for the timezone
    const [year, month, day] = dateStr.split('-');

    // Create ISO string in UTC, then adjust to server timezone
    const utcMidnight = Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0);

    // Get timezone offset for that date
    const testDate = new Date(utcMidnight);
    const tzFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false
    });

    const tzDate = new Date(tzFormatter.format(testDate));
    const offset = testDate.getTime() - tzDate.getTime();

    return utcMidnight + offset;
  } catch (e) {
    console.error('Error calculating start of day:', e);
    // Fallback to start of UTC day
    const d = new Date(dateMs);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0);
  }
}

async function fetchServerTimezone() {
  timelineDataLoaded.value = false;
  try {
    const r = await fetch(`${url.value}/api/timeline/${encodeURIComponent(cameraId.value)}/all`);
    if (r.ok) {
      const j = await r.json();
      serverTz.value = j.timezone || 'UTC';
      timelineData.value = j.days;
      timelineDataLoaded.value = true;

      // Initialize timeline position after loading data
      await initializeTimelinePosition();
    }
  } catch {
    void 0;
  }
}

// Initialize timeline position on first load
async function initializeTimelinePosition() {
  if (initialLoadComplete.value) return;
  initialLoadComplete.value = true;

  // Check if video is already playing
  const v = videoEl.value;
  const isPlaying = v && !v.paused && v.currentTime > 0;

  if (isPlaying) {
    // Video is playing - sync timeline with current video position
    // This will be handled by the Controls component's video sync
    console.log('Video already playing, will sync with playback');
    return;
  }

  // Check if timestamp is provided in URL query parameter
  const timestampParam = route.query.t;
  if (timestampParam) {
    const timestampMs = normalizeTimestampMs(timestampParam);
    if (Number.isFinite(timestampMs)) {
      const snappedMs = snapToNearestSegment(timestampMs, timelineData.value, serverTz.value);
      const dayStr = getDayString(snappedMs, serverTz.value);

      centerEpochMs.value = snappedMs;
      console.log('Initialized from URL to:', new Date(snappedMs).toISOString());

      await loadVideoAtTimestamp(snappedMs, dayStr);
      return;
    }
  }

  // No timestamp in URL - use default behavior (today's first segment or 00:00)
  const todayDate = new Date();
  const fmt = new Intl.DateTimeFormat(undefined, {
    timeZone: serverTz.value,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = fmt.formatToParts(todayDate);
  const todayStr = `${parts.find(p => p.type === 'year')?.value}-${parts.find(p => p.type === 'month')?.value}-${parts.find(p => p.type === 'day')?.value}`;

  // Find today's data
  const todayData = timelineData.value.find(d => (d.date || d.day) === todayStr);

  if (todayData && todayData.segments && todayData.segments.length > 0) {
    // Set to first segment of today and load video
    const firstSegment = todayData.segments[0];
    centerEpochMs.value = firstSegment.startMs;
    console.log('Initialized to first segment of today:', new Date(firstSegment.startMs).toISOString());

    // Load the video at this position
    await loadVideoAtTimestamp(firstSegment.startMs, todayStr);
  } else {
    // No segments today, set to 00:00 of today (but don't load video since there's nothing to play)
    const startOfDay = getStartOfDayInServerTz(Date.now(), serverTz.value);
    centerEpochMs.value = startOfDay;
    console.log('No segments today, initialized to 00:00:', new Date(startOfDay).toISOString());
  }
}

// Helper to load video at a specific timestamp
async function loadVideoAtTimestamp(timestampMs, dayStr) {
  try {
    await ensurePlayer();

    const startPositionSec = calculateVideoPosition(timestampMs, timelineData.value, serverTz.value);
    const info = {
      absoluteEpochMs: timestampMs,
      day: dayStr,
      startPositionSec,
      playlistUrl: `/hls/${cameraId.value}/day/${dayStr}/index.m3u8`,
      shouldReload: true,
    };

    await loadAndSeek(info);
  } catch (e) {
    console.error('Failed to load video on init:', e);
  }
}

// Update debug info periodically
function updateDebugInfo() {
  const v = videoEl.value;
  if (!v) return;

  debugInfo.value.currentTime = v.currentTime.toFixed(2);
  debugInfo.value.duration = v.duration ? v.duration.toFixed(2) : 'N/A';
  debugInfo.value.currentDay = currentDay.value;

  // Buffered ranges
  const buffered = [];
  for (let i = 0; i < v.buffered.length; i++) {
    buffered.push(`${v.buffered.start(i).toFixed(1)}-${v.buffered.end(i).toFixed(1)}`);
  }
  debugInfo.value.buffered = buffered.join(', ') || 'None';

  // Network state
  const networkStates = ['NETWORK_EMPTY', 'NETWORK_IDLE', 'NETWORK_LOADING', 'NETWORK_NO_SOURCE'];
  debugInfo.value.networkState = networkStates[v.networkState] || `Unknown (${v.networkState})`;

  // Ready state
  const readyStates = ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'];
  debugInfo.value.readyState = readyStates[v.readyState] || `Unknown (${v.readyState})`;

  // Video state
  if (v.ended) debugInfo.value.videoState = 'ended';
  else if (v.paused) debugInfo.value.videoState = 'paused';
  else if (isBuffering.value) debugInfo.value.videoState = 'buffering';
  else debugInfo.value.videoState = 'playing';

  // HLS info
  if (hlsRef.value) {
    debugInfo.value.hlsLevel = `${hlsRef.value.currentLevel} / ${hlsRef.value.levels?.length || 0} levels`;
  }

  // Timeline position in server timezone
  const timelineDate = new Date(centerEpochMs.value);
  const fmt = new Intl.DateTimeFormat(undefined, {
    timeZone: serverTz.value,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  debugInfo.value.timelinePosition = fmt.format(timelineDate);
}

// HLS error state
const hlsError = ref(null);

function updateNativeAudioState() {
  if (usingHls.value) return;
  if (detectVideoElementHasAudio(videoEl.value)) {
    hasAudioTrack.value = true;
  }
}

function updateHlsAudioState(data) {
  if (hasAudioTrack.value) return;
  if (detectHlsHasAudio(hlsRef.value, data)) {
    hasAudioTrack.value = true;
  }
}

function updateAudioStateFromVideo() {
  if (hasAudioTrack.value) return;
  if (detectVideoElementHasAudio(videoEl.value)) {
    hasAudioTrack.value = true;
  }
}

async function ensurePlayer() {
  const v = videoEl.value; if (!v) return;

  if (Hls.isSupported()) {
    if (!hlsRef.value) {
      hlsRef.value = new Hls({debug: false});
      usingHls.value = true;

      // Set up HLS error handler
      hlsRef.value.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS Error:', data.type, data.details, data);
        debugInfo.value.lastHlsError = `${data.type}:${data.details}`;
        
        if (data.fatal) {
          hlsError.value = `${data.type}: ${data.details}`;
          isBuffering.value = false;
        }
      });

      // Set up HLS event listeners for debug info
      hlsRef.value.on(Hls.Events.FRAG_CHANGED, (event, data) => {
        // Clear error on successful fragment change
        hlsError.value = null;
        debugInfo.value.lastHlsEvent = 'FRAG_CHANGED';
        
        const frag = data.frag;
        debugInfo.value.currentSegment = frag.url ? frag.url.split('/').pop() : 'N/A';

        if (frag.programDateTime) {
          const pdtDate = new Date(frag.programDateTime);
          const fmt = new Intl.DateTimeFormat(undefined, {
            timeZone: serverTz.value,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
          debugInfo.value.segmentPDT = fmt.format(pdtDate);
        } else {
          debugInfo.value.segmentPDT = 'Not available';
        }
      });

      // hlsRef.value.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
      //   debugInfo.value.hlsLevel = `${data.levels.length} levels available`;
      // });

      hlsRef.value.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        updateHlsAudioState(data);
      });

      hlsRef.value.on(Hls.Events.BUFFER_CODECS, (event, data) => {
        updateHlsAudioState(data);
      });

      hlsRef.value.on(Hls.Events.BUFFER_CREATED, (event, data) => {
        updateHlsAudioState(data);
      });

      hlsRef.value.on(Hls.Events.FRAG_PARSING_INIT_SEGMENT, (event, data) => {
        updateHlsAudioState(data);
      });
    }
  }
  else { usingHls.value = false; }
}

async function tryAutoplay() {
  const v = videoEl.value;
  if (!v) return;

  const played = await tryPlayerAutoplay(v, hasAudioTrack.value, isHovered.value);
  if (played) {
    autoPaused.value = false;
  } else {
    autoPaused.value = true;
    isBuffering.value = false;
  }
}

async function loadAndSeek(info) {
  const v = videoEl.value;
  if (!v) return;

  videoReady.value = false;
  hlsError.value = null;

  const fullUrl = `${url.value}${info.playlistUrl}`;
  const playlistChanged = currentPlaylistUrl !== fullUrl;
  if (playlistChanged) {
    hasAudioTrack.value = false;
  }
  debugInfo.value.playlistUrl = fullUrl;

  if (usingHls.value) {
    const hls = hlsRef.value;
    const startPositionSec = Number.isFinite(info.startPositionSec)
      ? info.startPositionSec
      : -1;

    if (currentPlaylistUrl !== fullUrl) {
      hls.loadSource(fullUrl);
      currentPlaylistUrl = fullUrl;
    }
    if (hls.media !== v) {
      hls.attachMedia(v);
    }

    if (startPositionSec >= 0) {
      v.currentTime = startPositionSec;
      console.log('Seeking to 1:', startPositionSec);
      const onParsed = () => {
        hls.off(Hls.Events.MANIFEST_PARSED, onParsed);
        if (Math.abs(v.currentTime - startPositionSec) > 0.5) {
          v.currentTime = startPositionSec;
          console.log('Seeked to:', startPositionSec);
        }
      };
      hls.on(Hls.Events.MANIFEST_PARSED, onParsed);
    }

    void tryAutoplay();
  } else {
    if (info.shouldReload || v.src !== info.playlistUrl) {
      v.src = fullUrl;
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            v.removeEventListener('loadedmetadata', onLoaded);
            v.removeEventListener('error', onError);
            reject(new Error('Video loading timeout'));
          }, 15000);
          
          const onLoaded = () => {
            clearTimeout(timeout);
            updateNativeAudioState();
            v.removeEventListener('loadedmetadata', onLoaded);
            v.removeEventListener('error', onError);
            resolve();
          };
          
          const onError = () => {
            clearTimeout(timeout);
            v.removeEventListener('loadedmetadata', onLoaded);
            v.removeEventListener('error', onError);
            reject(new Error('Video load error'));
          };
          
          v.addEventListener('loadedmetadata', onLoaded);
          v.addEventListener('error', onError);
        });
        updateNativeAudioState();
      } catch (err) {
        console.error('Failed to load video:', err.message);
        hlsError.value = err.message;
        isBuffering.value = false;
        return;
      }
    }
    if (Number.isFinite(info.startPositionSec) && info.startPositionSec >= 0) {
      v.currentTime = info.startPositionSec;
      console.log('Seeking to 2:', info.startPositionSec);
    }
    void tryAutoplay();
  }

  currentDay.value = info.day;
}

async function seekToEpoch(seekData) {
  await ensurePlayer();

  // Handle both old format (just epochMs) and new format (object with timestampMs and videoPositionSec)
  const epochMs = typeof seekData === 'number' ? seekData : seekData.timestampMs;
  const videoPositionSec = typeof seekData === 'object' ? seekData.videoPositionSec : null;
  const info = {
    absoluteEpochMs: epochMs,
    day: seekData.day,
    startPositionSec: videoPositionSec,
    playlistUrl: `/hls/${cameraId.value}/day/${seekData.day}/index.m3u8`,
    shouldReload: currentDay.value !== seekData.day,
  };
  await loadAndSeek(info);
}

// Keyboard shortcut handler
const handleKeyDown = (e) => {
  if (!store.isAdmin) return;
  // Ctrl+Shift+D or Cmd+Shift+D to toggle debug menu
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
    e.preventDefault();
    showDebugMenu.value = !showDebugMenu.value;
  }
};

// Debug info update interval
let debugUpdateInterval = null;

onMounted(async () => {
  try {
    if (division.value) {
      store.setCurrentDivision(division.value);
      store.setPageTitle(
        'Архив камеры: ' + division.value.name + ' / ' + cameraId.value
      )
    }
    await fetchServerTimezone();
  } catch (e) {
    console.error(e);
  }
  // Calculate timeline width based on video container width
  if (videoContainer.value) {
    timelineWidth.value = videoContainer.value.clientWidth - 32; // Account for padding
  }

  // Update timeline width on window resize
  window.addEventListener('resize', handleResize);

  // Set up keyboard shortcuts
  window.addEventListener('keydown', handleKeyDown);

  // Start debug info updates
  debugUpdateInterval = setInterval(updateDebugInfo, 500); // Update every 500ms

  // Set up video buffering state listeners
  if (videoEl.value) {
    const video = videoEl.value;

    const handleWaiting = () => {
      isBuffering.value = true;
    };

    const handleCanPlay = () => {
      isBuffering.value = false;
      videoReady.value = true;
    };

    const handlePlaying = () => {
      isBuffering.value = false;
      videoReady.value = true;
      autoPaused.value = false;
      updateAudioStateFromVideo();
    };

    const handleLoadedMetadata = () => {
      if (!usingHls.value) {
        updateNativeAudioState();
      } else {
        updateAudioStateFromVideo();
      }
    };

    const handleStalled = () => {
      isBuffering.value = true;
    };

    const handleLoadStart = () => {
      videoReady.value = false;
    };

    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('stalled', handleStalled);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    // Store handlers for cleanup
    video._bufferingHandlers = {
      waiting: handleWaiting,
      canplay: handleCanPlay,
      playing: handlePlaying,
      stalled: handleStalled,
      loadstart: handleLoadStart,
      loadedmetadata: handleLoadedMetadata,
    };
  }
});

const handleResize = () => {
  if (videoContainer.value) {
    timelineWidth.value = videoContainer.value.clientWidth - 32;
  }
};

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('keydown', handleKeyDown);

  // Clear debug update interval
  if (debugUpdateInterval) {
    clearInterval(debugUpdateInterval);
    debugUpdateInterval = null;
  }

  // Cleanup video buffering listeners
  if (videoEl.value && videoEl.value._bufferingHandlers) {
    const video = videoEl.value;
    const handlers = video._bufferingHandlers;
    video.removeEventListener('waiting', handlers.waiting);
    video.removeEventListener('canplay', handlers.canplay);
    video.removeEventListener('playing', handlers.playing);
    video.removeEventListener('stalled', handlers.stalled);
    video.removeEventListener('loadstart', handlers.loadstart);
    video.removeEventListener('loadedmetadata', handlers.loadedmetadata);
  }

  if (hlsRef.value) {
    try { hlsRef.value.destroy(); } catch {
      void 0;
    }
    hlsRef.value = null;
  }
});

// Retry loading after error
async function retryLoad() {
  hlsError.value = null;
  isBuffering.value = true;
  initialLoadComplete.value = false;
  await initializeTimelinePosition();
}

// Camera navigation
function onCamMove(direction) {
  const currentDivision = store.allDivisions.find(el => el.guid === divisionUuid.value);
  if (!currentDivision || !currentDivision.camList || currentDivision.camList.length === 0) {
    return;
  }

  const camList = currentDivision.camList;
  let currentIndex = camList.indexOf(cameraId.value);

  if (currentIndex === -1) {
    // If current camera not found, go to first camera
    currentIndex = 0;
  } else {
    if (direction === 'left') {
      currentIndex = currentIndex - 1;
      if (currentIndex < 0) {
        currentIndex = camList.length - 1; // Wrap to last
      }
    } else if (direction === 'right') {
      currentIndex = currentIndex + 1;
      if (currentIndex >= camList.length) {
        currentIndex = 0; // Wrap to first
      }
    }
  }

  const newCameraId = camList[currentIndex];

  // Navigate to new camera, preserving current timeline position
  router.push({
    name: 'cam-archive-better',
    params: {
      cam: newCameraId,
      division: divisionUuid.value
    },
    query: {
      t: centerEpochMs.value // Preserve timestamp
    }
  });
}

// Handle video zoom from Controls
function handleVideoZoom(zoomState) {
  if (!videoEl.value) return;

  const { level, pan, isPanning } = zoomState;

  // Update zoom level for indicator
  videoZoomLevel.value = level;

  // Apply CSS transform for zoom and pan
  const transform = `scale(${level}) translate(${pan.x}%, ${pan.y}%)`;
  videoEl.value.style.transform = transform;
  videoEl.value.style.transformOrigin = 'center center';

  // Disable transition during drag for immediate feedback, enable for zoom toggle
  if (isPanning) {
    videoEl.value.style.transition = 'none';
  } else {
    videoEl.value.style.transition = 'transform 0.15s ease-out';
  }
}

// Handle playback speed change from Controls
function handlePlaybackSpeed(speed) {
  if (!videoEl.value) return;

  // Set video playback rate
  videoEl.value.playbackRate = speed;
}
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: calc(100%);
  background: #0b0e13;
  color: #e8edf3;
}

.video-container {
  position: relative;
  flex: 1;
  max-height: calc(100% - 50px);
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video {
  width: 100%;
  height: 100%;
  background: #000;
  display: block;
  object-fit: contain;
}

.loading-spinner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  z-index: 9;
  pointer-events: none;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid #ffd166;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.error-display {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
  z-index: 15;
  padding: 24px;
  text-align: center;
}

.error-icon {
  font-size: 64px;
  color: #ff6b6b;
  margin-bottom: 16px;
}

.error-title {
  font-size: 20px;
  font-weight: 600;
  color: #e8edf3;
  margin-bottom: 12px;
}

.error-message {
  font-size: 14px;
  color: #8b9bb4;
  margin-bottom: 24px;
  max-width: 400px;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  background: rgba(255, 255, 255, 0.05);
  padding: 12px 16px;
  border-radius: 6px;
}

.error-retry {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #ffd166;
  border: none;
  border-radius: 8px;
  color: #0b0e13;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.error-retry:hover {
  background: #ffe599;
  transform: translateY(-2px);
}

.error-retry .mdi {
  font-size: 20px;
}

.timestamp-display {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #e8edf3;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
  backdrop-filter: blur(10px);
  z-index: 11;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.zoom-indicator {
  position: absolute;
  top: 16px;
  left: 16px;
  padding: 8px 16px;
  background: rgba(107, 139, 179, 0.9);
  border: 1px solid rgba(139, 163, 199, 0.5);
  border-radius: 6px;
  color: #ffffff;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
  backdrop-filter: blur(10px);
  z-index: 11;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.debug-menu {
  position: absolute;
  top: 16px;
  left: 16px;
  width: 500px;
  max-height: 80vh;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 209, 102, 0.3);
  border-radius: 8px;
  z-index: 20;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 209, 102, 0.1);
  border-bottom: 1px solid rgba(255, 209, 102, 0.3);
  color: #ffd166;
  font-weight: 600;
  font-size: 14px;
}

.debug-close {
  background: none;
  border: none;
  color: #ffd166;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.debug-close:hover {
  transform: scale(1.2);
}

.debug-content {
  overflow-y: auto;
  padding: 12px 16px;
  max-height: calc(80vh - 48px);
}

.debug-section {
  display: flex;
  margin-bottom: 12px;
  gap: 8px;
}

.debug-label {
  color: #888;
  min-width: 140px;
  flex-shrink: 0;
}

.debug-value {
  color: #fff;
  word-break: break-all;
  flex: 1;
}

.buffering-active {
  color: #ff6b6b;
  font-weight: 600;
}

.controls-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.6) 50%, transparent 100%);
  pointer-events: none;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.video-container:hover .controls-overlay {
  opacity: 1;
}

.controls-overlay>* {
  pointer-events: auto;
}

/* Show controls when video is paused or in fullscreen */
.video-container:has(video:paused) .controls-overlay,
.video-container:fullscreen .controls-overlay {
  opacity: 1;
}

/* Camera navigation buttons */
.camera-nav {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 5;
  padding: 0 20px;
}

.camera-nav-btn {
  pointer-events: auto;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: rgba(12, 16, 23, 0.75);
  backdrop-filter: blur(10px);
  color: #e8edf3;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  opacity: 0;
  transform: scale(0.8);
}

.camera-nav-btn .mdi {
  font-size: 32px;
  line-height: 1;
}

.video-container:hover .camera-nav-btn {
  opacity: 1;
  transform: scale(1);
}

.camera-nav-btn:hover {
  background: rgba(29, 38, 51, 0.9);
  color: #ffffff;
  transform: scale(1.1);
}

.camera-nav-btn:active {
  transform: scale(0.95);
}

.camera-nav-prev {
  margin-left: 0;
}

.camera-nav-next {
  margin-right: 0;
}

/* Mobile adjustments for camera nav */
@media (max-width: 768px) {
  .camera-nav {
    padding: 0 12px;
  }

  .camera-nav-btn {
    width: 48px;
    height: 48px;
  }

  .camera-nav-btn .mdi {
    font-size: 28px;
  }
}

/* Always show on touch devices when tapped */
@media (hover: none) {
  .camera-nav-btn {
    opacity: 0.7;
  }

  .video-container:active .camera-nav-btn {
    opacity: 1;
  }
}
</style>